import { Component, OnInit, ViewChild, ElementRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MediaService } from '../../services/http-services/media.service';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastService } from 'src/app/components/elements/toast/toast.service';
import { PackagesService } from '../../services/http-services/Packages/packages.service';

/**
 * CV manage modal — 3-step flow:
 * Pending → Received → In progress → Complete (upload deliverable closes; Reviewed skipped).
 */
@Component({
  selector: 'app-cv-template',
  templateUrl: './cv-template.component.html',
  styleUrls: ['./cv-template.component.scss']
})
export class CvTemplateComponent implements OnInit, OnChanges {
  @Input() cv: any;

  @ViewChild('initialFileInput', { static: false }) initialFileInput!: ElementRef;
  @ViewChild('updatedFileInput', { static: false }) updatedFileInput!: ElementRef;

  selectedCv: any = null;
  submissionFile: File | null = null;
  updatedCvFile: File | null = null;
  isWorking = false;

  constructor(
    private packagesService: PackagesService,
    private router: Router,
    private toastService: ToastService,
    public activeModal: NgbActiveModal,
    private mediaService: MediaService
  ) {}

  ngOnInit(): void {
    if (this.cv) {
      this.selectedCv = this.cv;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cv'] && changes['cv'].currentValue) {
      this.selectedCv = changes['cv'].currentValue;
    }
  }

  close(): void {
    this.activeModal.dismiss();
  }

  onFileSelected(event: any) {
    const file = event?.target?.files?.[0];
    if (file) this.submissionFile = file;
  }

  onUpdatedCvSelected(event: any) {
    const file = event?.target?.files?.[0];
    if (file) this.updatedCvFile = file;
  }

  onFileDropped(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer?.files?.length) {
      this.submissionFile = event.dataTransfer.files[0];
    }
  }

  onUpdatedCvDropped(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer?.files?.length) {
      this.updatedCvFile = event.dataTransfer.files[0];
    }
  }

  allowDragOver(event: DragEvent) {
    event.preventDefault();
  }

  isPending(): boolean {
    return this.selectedCv?.status === 0;
  }

  isReceived(): boolean {
    return this.selectedCv?.status === 1;
  }

  isInProgress(): boolean {
    return this.selectedCv?.status === 2;
  }

  /** Legacy Reviewed — treat as done; one-shot normalize to Complete via proceed. */
  isLegacyReviewed(): boolean {
    return this.selectedCv?.status === 3;
  }

  isCompleted(): boolean {
    return this.selectedCv?.status === 4 || this.isLegacyReviewed();
  }

  /** Upload final deliverable from Received or In progress. */
  canUploadDeliverable(): boolean {
    return this.isReceived() || this.isInProgress();
  }

  canProceed(): boolean {
    if (!this.selectedCv) return false;
    if (this.isPending()) return !!this.submissionFile;
    if (this.canUploadDeliverable()) return !!this.updatedCvFile || this.isReceived();
    if (this.isLegacyReviewed()) return true;
    return false;
  }

  getSubmissionLink(): string | null {
    return this.selectedCv?.submission || null;
  }

  getUpdatedSubmissionLink(): string | null {
    return this.selectedCv?.updatedSubmission || null;
  }

  viewSubmission() {
    const url = this.getSubmissionLink();
    if (!url) return;
    this.mediaService.getMediaUrl(url).subscribe((fullUrl) => {
      if (fullUrl) window.open(fullUrl, '_blank');
    });
  }

  viewUpdatedSubmission() {
    const url = this.getUpdatedSubmissionLink();
    if (!url) return;
    this.mediaService.getMediaUrl(url).subscribe((fullUrl) => {
      if (fullUrl) window.open(fullUrl, '_blank');
    });
  }

  proceed() {
    if (!this.selectedCv || !this.canProceed()) return;
    this.isWorking = true;

    if (this.isPending() && this.submissionFile) {
      this.uploadCV(this.submissionFile);
      return;
    }

    // Upload revised CV from Received or In progress → Complete
    if (this.canUploadDeliverable() && this.updatedCvFile) {
      this.uploadCV(this.updatedCvFile);
      return;
    }

    // Received without file → claim In progress; legacy Reviewed → Complete
    this.proceedToNextStatus();
  }

  private uploadCV(file: File) {
    const formData = new FormData();
    formData.append('ID', this.selectedCv.packageID);
    formData.append('UserID', this.selectedCv.userId || this.selectedCv.userID || '');
    formData.append('CV', file);

    this.packagesService.UpdateCV(formData).subscribe({
      next: () => {
        this.toastService.showSuccessMessage(
          this.isPending() ? 'CV uploaded successfully' : 'CV completed — deliverable uploaded'
        );
        this.isWorking = false;
        this.activeModal.close('uploaded');
      },
      error: (err) => {
        this.isWorking = false;
        this.toastService.showDangerMessage('Failed to upload CV');
        console.error(err);
      }
    });
  }

  private proceedToNextStatus(): void {
    this.packagesService.ProceedWithCV(this.selectedCv.packageID).subscribe({
      next: () => {
        if (this.isPending()) this.selectedCv.status = 1;
        else if (this.isReceived()) this.selectedCv.status = 2;
        else if (this.isLegacyReviewed()) this.selectedCv.status = 4;
        this.isWorking = false;
        this.toastService.showSuccessMessage('Progressed to next step');
        this.activeModal.close('proceeded');
      },
      error: (err) => {
        this.isWorking = false;
        this.toastService.showDangerMessage('Failed to proceed');
        console.error(err);
      }
    });
  }

  proceedButtonLabel(): string {
    if (this.isPending()) return 'Submit CV';
    if (this.canUploadDeliverable() && this.updatedCvFile) return 'Upload & Complete';
    if (this.isReceived()) return 'Start work';
    if (this.isLegacyReviewed()) return 'Mark Completed';
    if (this.isInProgress()) return 'Upload & Complete';
    return 'Proceed';
  }
}
